/**
 * The transcript list — the session's whole readable history, grouped one
 * section per player input (architecture.md §9; R7; R18-1/R18-2).
 *
 * @remarks
 * Pure and prop-driven: entries arrive as engine data (D23 committed them;
 * nothing here decides what belongs in the transcript). The list owns the
 * accessibility posture the single entry deliberately does not: it is a
 * `log` live region (`aria-live="polite"`), so screen readers announce new
 * lines as turns land without stealing focus from the input.
 *
 * R18 gave the log STRUCTURE for review, which announcement alone never had
 * (Calder: "passes axe" is not "is playable"): entries fold into one
 * `<section>` per input, each headed by the player's own echo as an `<h3>` —
 * the seam the eye already uses becomes the heading a screen reader jumps by
 * — under one visually-hidden `<h2>` so the outline nests below the app's
 * `<h1>`. Groups without an echo (the session opening; a blank input) carry
 * a visually-hidden generated heading instead. Scrolling remains the
 * CONTAINER'S concern — the app shell owns the viewport and pins it to the
 * bottom; this component just renders in order.
 *
 * @packageDocumentation
 */

import { groupTranscriptEntries, type RuntimeTranscriptEntry } from '@orpheus/engine-runtime';

import { entryRevealDurationOf, NO_SPEAKERS, type SpeakerPresentation, TranscriptEntryComponent } from '../transcript-entry/transcript-entry-component.js';

/** Props for {@link TranscriptListComponent}. */
export interface TranscriptListComponentProps {
	/** The transcript, oldest first — render exactly this, in exactly this order. */
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** D153: the ids of player echoes the clock typed — rendered muted. */
	readonly mutedEntryIds?: ReadonlySet<string>;
	/**
	 * R27: entries at this index and beyond are ARRIVING and perform their
	 * channel's reveal — except a line whose exact text already appeared
	 * earlier in the transcript, which renders finished (re-visits do not
	 * re-perform: a player re-reading a description to solve something is
	 * consulting prose, not experiencing it). Omitted, nothing animates —
	 * every other surface shows finished text.
	 */
	readonly revealFromIndex?: number;
	/** D192: the pack's declared speakers — each line's cadence and color are read from them. */
	readonly speakers?: readonly SpeakerPresentation[];
}

/**
 * Render the transcript as an announced log of turn-grouped entry components.
 *
 * @param props - {@link TranscriptListComponentProps}
 * @returns The rendered transcript log
 */
/**
 * D102: the breath between one arriving line finishing and the next
 * beginning — lines land as a sequence, not a wall, and the pause is what
 * makes the sequence readable as one voice yielding to the next.
 */
const INTER_LINE_BREATH_MS = 250;

/** The reveal's schedule for one render: when each arriving line begins, and when the last one ends. */
export interface TranscriptRevealPlan {
	/** Each arriving entry's start, measured from the turn's start. */
	readonly revealStartDelaysMsById: ReadonlyMap<string, number>;
	/** When the last arriving line has finished — zero when nothing arrives. */
	readonly totalMs: number;
}

/**
 * Plan the arriving lines' reveal (R27/D102): which entry ids are arriving
 * (at or past the reveal boundary, and the FIRST appearance of their exact
 * text this session) and WHEN each begins — a turn's lines animate in ORDER,
 * so each arriving line starts where its predecessors end, plus a breath.
 *
 * Exported for the clock (D153), which must DEFER a turn while the reveal is
 * still typing: the player has not been able to read the last reading yet.
 *
 * @param entries - The transcript, oldest first
 * @param revealFromIndex - The reveal boundary; omitted, nothing arrives
 * @param speakers - The pack's declared speakers, for a speaker line's pace (D192)
 * @returns The plan
 */
export const planTranscriptReveal = (
	entries: readonly RuntimeTranscriptEntry[],
	revealFromIndex?: number,
	speakers: readonly SpeakerPresentation[] = NO_SPEAKERS,
): TranscriptRevealPlan => {
	const seenTexts = new Set<string>();
	const revealStartDelaysMsById = new Map<string, number>();
	let cumulativeDelayMs = 0;

	entries.forEach((entry, entryIndex) => {
		if (revealFromIndex !== undefined && entryIndex >= revealFromIndex && !seenTexts.has(entry.text)) {
			const revealDurationMs = entryRevealDurationOf(entry, speakers);

			if (revealDurationMs > 0) {
				revealStartDelaysMsById.set(entry.id, cumulativeDelayMs);
				cumulativeDelayMs += revealDurationMs + INTER_LINE_BREATH_MS;
			}
		}
		seenTexts.add(entry.text);
	});

	return { revealStartDelaysMsById, totalMs: cumulativeDelayMs };
};

export const TranscriptListComponent = ({ entries, mutedEntryIds, revealFromIndex, speakers = NO_SPEAKERS }: TranscriptListComponentProps) => {
	const { revealStartDelaysMsById } = planTranscriptReveal(entries, revealFromIndex, speakers);

	return (
		<div aria-label="Transcript" className="transcript-list" role="log">
			<h2 className="visually-hidden">Transcript</h2>
			{groupTranscriptEntries(entries).map((group) => (
				<section className="transcript-turn" key={group.key}>
					{group.headingEntry === undefined ? (
						<h3 className="visually-hidden">{group.headingText}</h3>
					) : (
						<TranscriptEntryComponent as="h3" entry={group.headingEntry} isMuted={mutedEntryIds?.has(group.headingEntry.id)} speakers={speakers} />
					)}
					{group.entries.map((entry) => (
						<TranscriptEntryComponent
							entry={entry}
							isArriving={revealStartDelaysMsById.has(entry.id)}
							key={entry.id}
							revealStartDelayMs={revealStartDelaysMsById.get(entry.id)}
							speakers={speakers}
						/>
					))}
				</section>
			))}
		</div>
	);
};
